import { Button } from '@fluentui/react-components';
import { useConvertToFile, useConvertToTemplate, useEnableAttachments, useTemplateUpdate } from '../../../api';
import { TDocument, TDraft, TVersion } from '../../../types';
import { TFile, TFileVersion } from '@modules/files';
import { useUpdateVersionDialog } from '@modules/items/hooks/useUpdateVersionDialog.hook';
import {
  ArrowSortRegular,
  FlashOffRegular,
  FlashRegular,
  AttachRegular,
  CalendarCancelRegular,
  CalendarClockRegular,
  PlugConnectedAddRegular,
} from '@fluentui/react-icons';
import {
  ButtonLoading,
  ConfirmMenu,
  ControlledTooltip,
  OptionsDialog,
  SingleFieldDialog,
  TSingleFieldDialogState,
} from '@shared/components';
import { useContext, useState } from 'react';
import FileIcon from '@modules/items/components/FileIcon';
import { ITEM_MIMETYPES } from '@modules/items/constants';
import { SignInContext } from '@modules/users';
import { DisabledFeature } from '@modules/users/types/organizations.types';
import { TPermissions, useCanEdit } from '@modules/access';
import { TERMINOLOGY } from '@shared/i18n';
import DocumentContext from '../../../contexts/DocumentContext.context';
import {
  SelectItemDialog,
  TSelectItemDialogState,
  EBindingMode,
  isTemplateSelectable,
  getTemplateDisabledReason,
  TEMPLATE_SEARCH_OPTIONS,
  getBindingModeOptions,
  TPeriodicReviewItemType,
  useSetPeriodicReviewEnabled,
} from '@modules/items';
import { TOptionsDialogState } from '@shared/types';
import { ToastContext } from '@shared/contexts';
import { isHeavyEnoughForParts, isPartLoadable } from '../../../largeDocument';
import { useFastLoadingStore } from '../../../largeDocument';

type TProps = {
  item: TDocument | TFile;
  isDoc: boolean;
  version: TVersion | TDraft | TFileVersion;
  versionId: string;
  permissions: TPermissions;
  closeSettingsDialog?: () => void;
  openPeriodicReviewDialog?: () => void;
};

const AdvancedActions = ({
  item,
  isDoc,
  version,
  versionId,
  permissions,
  closeSettingsDialog,
  openPeriodicReviewDialog,
}: TProps) => {
  const [simpleDialogState, setSimpleDialogState] = useState<TSingleFieldDialogState<'input'> | undefined>(undefined);
  const { organization } = useContext(SignInContext);
  const { show } = useContext(ToastContext);
  const documentContext = useContext(DocumentContext); // nullable - AdvancedActions is also used for files
  const { enabled: fastLoading, setEnabled: setFastLoading } = useFastLoadingStore();
  /**
   * Offered only for a document that is both eligible and heavy enough to benefit. Eligibility has to
   * be asked as well as size, or reusable content - which never loads in parts, whatever its size -
   * offers a button that does nothing. The switch itself is per user, not per document: turning it on
   * here applies to every large document this browser opens.
   */
  const showFastLoading = isDoc && isPartLoadable(item as TDocument) && isHeavyEnoughForParts(documentContext?.content);
  const doc = item as TDocument; // safe - guarded by `isDoc` checks below
  const itemType: TPeriodicReviewItemType = isDoc ? 'DOCUMENT' : 'FILE';
  const { canEdit, editLockedReason } = useCanEdit(permissions.accessRight);
  const { canEdit: canEditWithoutAuthorGroup, editLockedReason: versionLockedReason } = useCanEdit(
    permissions.accessRight,
    { authorGroup: false }
  );

  // update version:
  const showUpdateVersion =
    !doc.isBlock &&
    versionId !== 'draft' &&
    versionId === item.stats.latestVersionId &&
    versionId !== item.stats.approvedVersionId;
  const updateVersionDisabledReason = !showUpdateVersion
    ? doc.isBlock
      ? `Version updates are not available for ${TERMINOLOGY.block.lower}.`
      : versionId === 'draft'
        ? 'Complete the draft first to create a version.'
        : versionId !== item.stats.latestVersionId
          ? 'Select the latest version to update its number.'
          : versionId === item.stats.approvedVersionId
            ? 'The approved version number cannot be changed.'
            : undefined
    : versionLockedReason || undefined;
  const { open: openUpdateVersionDialog } = useUpdateVersionDialog();
  const handleUpdateVersion = () =>
    openUpdateVersionDialog(
      item,
      isDoc ? 'DOCUMENT' : 'FILE',
      version as TVersion | TFileVersion,
      setSimpleDialogState,
      simpleDialogState
    );

  // convert to file:
  const showConvertToFile = isDoc && canEdit && item.versions.length <= 0 && Boolean(doc.importDetails?.blobName);
  const convertToFile = useConvertToFile();
  const handleConvertToFile = async () => {
    // Save & disconnect WebSocket before archiving
    if (documentContext?.isEdit && !(await documentContext.viewMode())) return;
    convertToFile.mutate({ id: item._id });
  };

  const convertToTemplate = useConvertToTemplate();
  const templatesEnabled = organization.featureSettings.templates?.enabled;
  const showConvertToTemplate = isDoc && templatesEnabled && !doc.isTemplate && !doc.isBlock;
  const convertToTemplateDisabledReason = doc.templateId
    ? `Documents with an applied ${TERMINOLOGY.template.lower} cannot be converted.`
    : editLockedReason || undefined;
  const convertToTemplateEnabled = showConvertToTemplate && !convertToTemplateDisabledReason;
  const handleConvertToTemplate = () => {
    convertToTemplate.mutate(
      { id: item._id },
      {
        onSuccess: () => {
          setSimpleDialogState(simpleDialogState ? { ...simpleDialogState, isOpen: false } : undefined);
        },
      }
    );
  };

  // bind to template:
  const templateBindingEnabled = organization.featureSettings.templateBinding?.enabled;
  const applyTemplateDisabled = organization.mock.disabledFeatures?.includes(DisabledFeature.APPLY_TEMPLATE);
  const showBindToTemplate =
    isDoc && templateBindingEnabled && !applyTemplateDisabled && !doc.isTemplate && !doc.isBlock && !doc.templateId;
  const bindToTemplateDisabledReason = doc.draft ? 'Complete the open draft first.' : undefined;
  const bindToTemplateEnabled = showBindToTemplate && !bindToTemplateDisabledReason;
  const enableAttachments = useEnableAttachments();
  const showEnableAttachments = isDoc && !doc.isBlock && doc.attachments === undefined;
  const hasAttachmentEditGate = Boolean(documentContext && canEdit && !documentContext.replica);
  const enableAttachmentsDisabledReason = documentContext?.replica
    ? `${TERMINOLOGY.synchronized.upper} documents can't be edited.`
    : documentContext && editLockedReason
      ? editLockedReason
      : undefined;

  const [templateDialogState, setTemplateDialogState] = useState<TSelectItemDialogState | undefined>(undefined);
  const [bindingModeDialog, setBindingModeDialog] = useState<TOptionsDialogState | undefined>(undefined);
  const templateUpdate = useTemplateUpdate();
  const setPeriodicReviewEnabled = useSetPeriodicReviewEnabled();

  const handleEnablePeriodicReview = async () => {
    await setPeriodicReviewEnabled.mutateAsync({ id: item._id, itemType, enabled: true });
    closeSettingsDialog?.();
    openPeriodicReviewDialog?.();
  };

  const handleDisablePeriodicReview = () => {
    setPeriodicReviewEnabled.mutate({ id: item._id, itemType, enabled: false });
  };

  const bindWithMode = (mode: EBindingMode, templateId: string) => {
    setBindingModeDialog(undefined);
    templateUpdate.mutate({
      id: item._id,
      templateId,
      bindingMode: mode,
    });
  };

  // No extra confirmation dialog: the multi-step flow (select template → choose mode) is intentional enough.
  const handleBindToTemplate = () => {
    setTemplateDialogState({
      isOpen: true,
      onSelect: async ([template]) => {
        setTemplateDialogState(undefined);

        setBindingModeDialog({
          isOpen: true,
          title: `Apply ${TERMINOLOGY.template.lower}`,
          label: 'Choose mode',
          options: getBindingModeOptions(
            () => bindWithMode(EBindingMode.BOUND, template._id),
            () => bindWithMode(EBindingMode.DETACHED, template._id)
          ),
          selectedValue: undefined,
        });
      },
    });
  };

  const path = item.resolvedPath.map((pathItem) => pathItem._id);
  return (
    <div className="flex flex-col space-y-4">
      <SingleFieldDialog state={simpleDialogState} setState={setSimpleDialogState} />
      <OptionsDialog state={bindingModeDialog} setState={setBindingModeDialog} />
      <SelectItemDialog
        initialFolderId={item.resolvedPath[0]._id}
        state={templateDialogState}
        setState={setTemplateDialogState}
        title={`Select a ${TERMINOLOGY.template.lower}`}
        actionText="Select"
        navigatableFolderTypes={['NORMAL', 'COUNTRY']}
        selectableTypes={['DOCUMENT']}
        isSelectable={isTemplateSelectable}
        getDisabledReason={getTemplateDisabledReason}
        enableSearch
        searchOptions={TEMPLATE_SEARCH_OPTIONS}
      />
      {canEdit && (
        <div className="space-y-1">
          <p>
            {item.periodicReview?.enabled
              ? 'Turn off periodic review scheduling for this item.'
              : 'Turn on periodic review scheduling for this item.'}
          </p>
          {item.periodicReview?.enabled ? (
            <ConfirmMenu
              onConfirm={handleDisablePeriodicReview}
              warningText="Disable periodic review for this item? Review history will be kept."
            >
              <ButtonLoading
                icon={<CalendarCancelRegular />}
                loading={setPeriodicReviewEnabled.isPending}
                disabled={setPeriodicReviewEnabled.isPending}
              >
                Disable periodic review
              </ButtonLoading>
            </ConfirmMenu>
          ) : (
            <ButtonLoading
              icon={<CalendarClockRegular />}
              loading={setPeriodicReviewEnabled.isPending}
              onClick={handleEnablePeriodicReview}
            >
              Enable periodic review
            </ButtonLoading>
          )}
        </div>
      )}

      {showConvertToFile && (
        <div className="space-y-1">
          <p>Convert Word document to static Word file.</p>
          <ConfirmMenu
            onConfirm={handleConvertToFile}
            warningText="This document will be archived and replaced by a static file. Are you sure?"
          >
            <ButtonLoading
              icon={
                <FileIcon
                  type="FILE"
                  path={path}
                  isBlock={false}
                  isFrozen={false}
                  isTemplate={false}
                  mimetype={ITEM_MIMETYPES.docx[0]}
                  size="small"
                />
              }
              loading={convertToFile.isPending}
            >
              Convert to static file
            </ButtonLoading>
          </ConfirmMenu>
        </div>
      )}

      {showConvertToTemplate && (
        <div className="space-y-1">
          <p>Convert this document into a {TERMINOLOGY.template.lower}.</p>
          {/* Don't nest ControlledTooltip and ConfirmMenu - both clone their child to attach
             refs/events, so the outer one's handlers get silently dropped. Render one or the other. */}
          {convertToTemplateEnabled ? (
            <ConfirmMenu
              onConfirm={handleConvertToTemplate}
              warningText={`This document will be marked as a ${TERMINOLOGY.template.lower}. Continue?`}
            >
              <ButtonLoading
                icon={<FileIcon type="DOCUMENT" path={path} isBlock={false} isFrozen={false} isTemplate size="small" />}
                loading={convertToTemplate.isPending}
              >
                Convert to {TERMINOLOGY.template.lower}
              </ButtonLoading>
            </ConfirmMenu>
          ) : (
            <ControlledTooltip
              enable={!!convertToTemplateDisabledReason}
              content={convertToTemplateDisabledReason ?? ''}
              relationship="label"
              positioning="before"
              withArrow
            >
              <ButtonLoading
                icon={<FileIcon type="DOCUMENT" path={path} isBlock={false} isFrozen={false} isTemplate size="small" />}
                disabled
              >
                Convert to {TERMINOLOGY.template.lower}
              </ButtonLoading>
            </ControlledTooltip>
          )}
        </div>
      )}

      {showBindToTemplate && (
        <div className="space-y-1">
          <p>Apply a {TERMINOLOGY.template.lower} to this document.</p>
          <ControlledTooltip
            enable={!!bindToTemplateDisabledReason}
            content={bindToTemplateDisabledReason ?? ''}
            relationship="label"
            positioning="before"
            withArrow
          >
            <ButtonLoading
              icon={<PlugConnectedAddRegular />}
              loading={templateUpdate.isPending}
              disabled={!bindToTemplateEnabled}
              onClick={handleBindToTemplate}
            >
              Apply {TERMINOLOGY.template.lower}
            </ButtonLoading>
          </ControlledTooltip>
        </div>
      )}

      {showEnableAttachments && (
        <div className="space-y-1">
          <p>Enable PDF attachments for this document.</p>
          <ControlledTooltip
            enable={!!enableAttachmentsDisabledReason}
            content={enableAttachmentsDisabledReason ?? ''}
            relationship="label"
            positioning="before"
            withArrow
          >
            <ButtonLoading
              icon={<AttachRegular />}
              loading={enableAttachments.isPending}
              disabled={!hasAttachmentEditGate}
              onClick={() => enableAttachments.mutate({ id: item._id })}
            >
              Enable attachments
            </ButtonLoading>
          </ControlledTooltip>
        </div>
      )}

      <div className="space-y-1">
        <p>Change the version number.</p>
        <ControlledTooltip
          enable={!!updateVersionDisabledReason}
          content={updateVersionDisabledReason ?? ''}
          relationship="label"
          positioning="before"
          withArrow
        >
          <Button
            icon={<ArrowSortRegular />}
            disabled={!showUpdateVersion || !canEditWithoutAuthorGroup}
            onClick={handleUpdateVersion}
          >
            Update version
          </Button>
        </ControlledTooltip>
      </div>

      {showFastLoading && (
        <div className="space-y-1">
          <p>
            {fastLoading ? 'Large documents load a section at a time.' : 'Load large documents a section at a time.'}
          </p>
          <ConfirmMenu
            onConfirm={async () => {
              if (documentContext?.isEdit && !(await documentContext.save({ forceSave: true }))) {
                show('error', 'Your changes could not be saved, so the page was not reloaded.');
                return;
              }
              setFastLoading(!fastLoading);
              window.location.reload();
            }}
            warningText="The page reloads to apply this. Any unsaved changes are saved first."
          >
            <Button icon={fastLoading ? <FlashOffRegular /> : <FlashRegular />}>
              {fastLoading ? 'Disable fast loading' : 'Enable fast loading'}
            </Button>
          </ConfirmMenu>
        </div>
      )}
    </div>
  );
};

export default AdvancedActions;
